import { useLazyQuery } from "@apollo/client/react";
import { useCallback } from "react";
import { useStore } from "@/app/store";
import { VARIABLE_DEFINITIONS } from "../graphql/queries";

export interface VariableDefinitionOption {
  code: string;
  displayName: string;
}

const toPool = (
  raw: readonly VariableDefinitionOption[] | undefined,
): VariableDefinitionOption[] =>
  [...(raw ?? [])].sort((a, b) => a.displayName.localeCompare(b.displayName));

const useVariableDefinitions = () => {
  const variables = useStore((s) => s.variableDefinitions);
  const error = useStore((s) => s.variableDefinitionsError);

  return {
    variables: variables ?? [],
    loading: variables === null && error === null,
    error,
  };
};

export const useVariableDefinitionsPrefetch = () => {
  const [fetchVariableDefinitions] = useLazyQuery(VARIABLE_DEFINITIONS);

  return useCallback(() => {
    if (useStore.getState().variableDefinitions !== null) return;

    const storeError = (e: unknown) =>
      useStore
        .getState()
        .setVariableDefinitionsError(
          e instanceof Error ? e : new Error("Failed to load variables"),
        );

    void fetchVariableDefinitions()
      .then((res) => {
        if (res.error) {
          storeError(res.error);
          return;
        }
        useStore
          .getState()
          .setVariableDefinitions(toPool(res.data?.variableDefinitions));
      })
      .catch(storeError);
  }, [fetchVariableDefinitions]);
};

export default useVariableDefinitions;
